import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  TextField,
  Button,
  Chip
} from '@mui/material';
import {
  LocalShipping,
  Refresh,
  Print,
  Storefront as StorefrontIcon,
  Build as BuildIcon,
} from '@mui/icons-material';
import { getDailyTonerDispatchReport } from '../services/api';
import { DailyTonerDispatchReport as ReportType } from '../types/reports';
import { getPanamaDateString, formatDateForDisplay } from '../utils/dateUtils';

export default function DailyTonerDispatchReport() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<ReportType | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Date filters - use Panama timezone
  const today = getPanamaDateString();
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);

  useEffect(() => {
    loadReport();
  }, []);

  const loadReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getDailyTonerDispatchReport(startDate, endDate);
      setReport(data);
    } catch (error: any) {
      console.error('Error loading toner dispatch report:', error);
      setError(error.response?.data?.detail || 'Error loading report data');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadReport();
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (num: number) => {
    return new Intl.NumberFormat('es-PA', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
            Reporte de Despacho de Tóner
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<Print />}
              onClick={handlePrint}
              className="no-print"
            >
              Imprimir
            </Button>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={loadReport}
              className="no-print"
            >
              Actualizar
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Date Filter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ flex: '1 1 300px' }}>
                <TextField
                  label="Fecha Inicial"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <TextField
                  label="Fecha Final"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box sx={{ flex: '1 1 300px' }}>
                <Button
                  variant="contained"
                  onClick={handleApplyFilters}
                  fullWidth
                  sx={{ height: '56px' }}
                >
                  Aplicar Filtros
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {report && (
          <>
            {/* Summary Cards - Row 1: Totals */}
            <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Total Despachos
                    </Typography>
                    <Typography variant="h4">
                      {report.grand_total_dispatches}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      unidades
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Costo Total
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {formatCurrency(report.grand_total_cost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card sx={{ backgroundColor: '#e3f2fd' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Costo OEM
                    </Typography>
                    <Typography variant="h5" color="primary">
                      {formatCurrency(report.grand_total_oem_cost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card sx={{ backgroundColor: '#f3e5f5' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Costo Genérico
                    </Typography>
                    <Typography variant="h5" color="secondary">
                      {formatCurrency(report.grand_total_generic_cost)}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Summary Cards - Row 2: Parts vs Consumables */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card sx={{ backgroundColor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Consumibles
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#e65100' }}>
                      {formatCurrency(report.grand_total_consumables_cost)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.grand_total_consumables_dispatches} unidades
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card sx={{ backgroundColor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Partes/Repuestos
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#2e7d32' }}>
                      {formatCurrency(report.grand_total_parts_cost)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.grand_total_parts_dispatches} unidades
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Summary Cards - Row 3: Service Dispatches vs Initial Kits (MFP Sales) */}
            <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card sx={{ backgroundColor: '#e8eaf6', borderLeft: '4px solid #3f51b5' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      <BuildIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Despachos de Servicio
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#283593' }}>
                      {formatCurrency(report.grand_total_service_cost ?? 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.grand_total_service_dispatches ?? 0} unidades
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <Card sx={{ backgroundColor: '#fce4ec', borderLeft: '4px solid #e91e63' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      <StorefrontIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                      Kit Inicial (Venta MFP)
                    </Typography>
                    <Typography variant="h5" sx={{ color: '#c2185b' }}>
                      {formatCurrency(report.grand_total_initial_kit_cost ?? 0)}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {report.grand_total_initial_kit_dispatches ?? 0} unidades (incluido en precio de venta)
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Box>

            {/* Daily Summary Table */}
            {report.summary.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Resumen por Día
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Fecha</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                          <TableCell align="right"><strong>Costo Total</strong></TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}><strong>Consumibles</strong></TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#fff3e0' }}><strong>Costo Cons.</strong></TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#e8f5e9' }}><strong>Partes</strong></TableCell>
                          <TableCell align="right" sx={{ backgroundColor: '#e8f5e9' }}><strong>Costo Partes</strong></TableCell>
                          <TableCell align="right"><strong>OEM</strong></TableCell>
                          <TableCell align="right"><strong>Genérico</strong></TableCell>
                          <TableCell align="right"><strong>Clientes</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.summary.map((day, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{formatDateForDisplay(day.dispatch_date)}</TableCell>
                            <TableCell align="right">{day.total_dispatches}</TableCell>
                            <TableCell align="right">{formatCurrency(day.total_cost)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8e1' }}>{day.consumables_dispatches}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#fff8e1' }}>{formatCurrency(day.consumables_cost)}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f1f8e9' }}>{day.parts_dispatches}</TableCell>
                            <TableCell align="right" sx={{ backgroundColor: '#f1f8e9' }}>{formatCurrency(day.parts_cost)}</TableCell>
                            <TableCell align="right">{day.oem_dispatches}</TableCell>
                            <TableCell align="right">{day.generic_dispatches}</TableCell>
                            <TableCell align="right">{day.unique_clients}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Detailed Dispatches Table */}
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detalles de Despachos
                </Typography>
                {report.dispatches.length === 0 ? (
                  <Typography color="textSecondary" align="center" sx={{ py: 3 }}>
                    No hay despachos en el período seleccionado
                  </Typography>
                ) : (
                  <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Fecha</strong></TableCell>
                          <TableCell><strong>Cliente</strong></TableCell>
                          <TableCell><strong>Equipo</strong></TableCell>
                          <TableCell><strong>Contrato</strong></TableCell>
                          <TableCell><strong>Ubicación</strong></TableCell>
                          <TableCell><strong>Suministro</strong></TableCell>
                          <TableCell><strong>Categoría</strong></TableCell>
                          <TableCell><strong>Origen</strong></TableCell>
                          <TableCell><strong>OEM/Gen</strong></TableCell>
                          <TableCell align="right"><strong>Cantidad</strong></TableCell>
                          <TableCell align="right"><strong>Costo Unit.</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                          <TableCell><strong>Despachado Por</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.dispatches.map((dispatch, index) => (
                          <TableRow key={index} hover sx={dispatch.is_initial_kit ? { backgroundColor: '#fce4ec40' } : undefined}>
                            <TableCell>{formatDateForDisplay(dispatch.assigned_date)}</TableCell>
                            <TableCell>{dispatch.client_name}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{dispatch.equipment_serial}</Typography>
                              {dispatch.equipment_model && (
                                <Typography variant="caption" color="textSecondary">
                                  {dispatch.equipment_model}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{dispatch.contract_number || '-'}</Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="textSecondary">
                                {dispatch.location || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{dispatch.supply_name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                SKU: {dispatch.supply_sku}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={dispatch.supply_type === 'part' ? 'Parte' : 'Consumible'}
                                sx={{
                                  backgroundColor: dispatch.supply_type === 'part' ? '#e8f5e9' : '#fff3e0',
                                  color: dispatch.supply_type === 'part' ? '#2e7d32' : '#e65100',
                                }}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={dispatch.is_initial_kit ? 'Kit Inicial' : 'Servicio'}
                                sx={{
                                  backgroundColor: dispatch.is_initial_kit ? '#fce4ec' : '#e8eaf6',
                                  color: dispatch.is_initial_kit ? '#c2185b' : '#283593',
                                }}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={dispatch.is_original ? 'OEM' : 'Gen'}
                                color={dispatch.is_original ? 'primary' : 'secondary'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">{dispatch.quantity}</TableCell>
                            <TableCell align="right">{formatCurrency(dispatch.cost_per_unit)}</TableCell>
                            <TableCell align="right">
                              <strong>{formatCurrency(dispatch.total_cost)}</strong>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {dispatch.assigned_by || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </>
  );
}
